import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  CircularProgress,
  LinearProgress,
  TableCell,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { IconTrash, IconEditPen } from '@shared/assets/images/svgs';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { EditAttributesMetadataModal } from './components/EditAttributesMetadataModal';
import CustomAlert from '@shared/components/alert/alert';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { CustomErrorToast } from '@shared/components/customErrorToast';
import * as XLSX from 'xlsx';
import { isValidSipDialString, normalizeSipDialString } from '@shared/utils/sipDialString';
import { useFormContext } from 'react-hook-form';
import { CreateCampaignFormValue, NumbersList } from '@features/Admin/Campaign/model/type';
import { NumberListToCustomersMapper } from '@features/Admin/Campaign/utils/lead-customer-mapper';
import { RootState, useAppSelector } from '@shared/store';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { CALL_TYPES } from '@widgets/Popup/TestingAgentPopup';
import { formatCallType } from '@pages/Admin/Campaign/ui/utils';
import { CommonTable, CommonTableColumn } from '@shared/components/commonTable/CommonTable';
import { CustomerListItemDto, apiService } from '@shared/services/apiService/apiService';

const { useLazyQuery: useLazyCustomersPaginatedQuery } =
  apiService.endpoints.customerListPaginatedControllerGetCustomerListPaginated;

interface PhoneNumber {
  id: string;
  number: string;
  createdAt?: string;
  dateAdded?: string;
  name: string | null;
  attributes?: Record<string, any>;
  metadata?: Record<string, any>;
}

interface PhoneNumberListProps {
  campaignId?: string;
  setCustomersListChanged: (changed: boolean) => void;
  responseLoadingFlag: boolean;
  isCampaignActive?: boolean;
}

const mapApiItemToPhoneNumber = (item: CustomerListItemDto): PhoneNumber => ({
  id: item.id,
  number: item.number || '',
  name: item.name || (item.attributes as Record<string, string> | undefined)?.name || null,
  dateAdded: item.createdAt
    ? new Date(item.createdAt).toLocaleDateString()
    : new Date().toLocaleDateString(),
  attributes: item.attributes as Record<string, string> | undefined,
  metadata: item.metadata as Record<string, string> | undefined,
});

export const PhoneNumberList: React.FC<PhoneNumberListProps> = ({
  campaignId,
  setCustomersListChanged,
  responseLoadingFlag,
  isCampaignActive = false,
}) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<CreateCampaignFormValue>();
  const currentUser = useAppSelector((state: RootState) => state.auth.authenticatedUser);

  const [pagedCustomers, setPagedCustomers] = useState<PhoneNumber[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'createdAt' | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  const [selectedNumbers, setSelectedNumbers] = useState<string[]>([]);
  const [showDialog, setShowDialog] = useState(false);
  const [deleteIds, setDeleteIds] = useState<string[]>([]);
  const [availableToImport, setAvailableToImport] = useState<boolean>(true);
  const [responseMsg] = useState({
    message: '',
    type: 'success' as 'success' | 'error',
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingNumber, setEditingNumber] = useState<PhoneNumber | null>(null);
  const [searchText, setSearchText] = useState('');

  const loadMoreAnchorRef = useRef<HTMLDivElement | null>(null);
  const activeCampaignIdRef = useRef<string | undefined>(undefined);

  const [fetchPage, { isFetching }] = useLazyCustomersPaginatedQuery();

  useEffect(() => {
    setAvailableToImport(!responseLoadingFlag);
  }, [responseLoadingFlag]);

  const handleSort = (column: 'name' | 'createdAt') => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const getSortDirection = (column: 'name' | 'createdAt'): 'asc' | 'desc' =>
    sortBy === column ? (sortOrder.toLowerCase() as 'asc' | 'desc') : 'asc';

  const SortIcon = ({ className }: { className?: string }) => (
    <Box
      className={className}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        borderRadius: '50%',
        bgcolor: 'action.selected',
        ml: 0.5,
        flexShrink: 0,
        transition: 'background-color 0.2s',
        '& svg': { fontSize: 12 },
      }}>
      <ArrowUpwardIcon sx={{ fontSize: 12 }} />
    </Box>
  );

  useEffect(() => {
    if (!campaignId) return;

    const isCampaignChange = activeCampaignIdRef.current !== campaignId;
    activeCampaignIdRef.current = campaignId;

    setPagedCustomers([]);
    setCursor(null);
    setHasMore(false);
    setSelectedNumbers([]);

    if (isCampaignChange) {
      setIsInitialLoading(true);
    }

    fetchPage({
      campaignId,
      limit: 20,
      sortBy: sortBy ?? 'createdAt',
      sortOrder: sortBy ? sortOrder : 'DESC',
    })
      .unwrap()
      .then((res) => {
        setPagedCustomers(res.data.map(mapApiItemToPhoneNumber));
        setCursor(res.meta.cursor);
        setHasMore(res.meta.hasMore);
      })
      .catch(() => {})
      .finally(() => setIsInitialLoading(false));
  }, [campaignId, sortBy, sortOrder]);

  const loadMore = useCallback(() => {
    if (!campaignId || !cursor || !hasMore || isFetching) return;

    fetchPage({
      campaignId,
      limit: 20,
      cursor,
      sortBy: sortBy ?? 'createdAt',
      sortOrder: sortBy ? sortOrder : 'DESC',
    })
      .unwrap()
      .then((res) => {
        setPagedCustomers((prev) => [...prev, ...res.data.map(mapApiItemToPhoneNumber)]);
        setCursor(res.meta.cursor);
        setHasMore(res.meta.hasMore);
      })
      .catch(() => {});
  }, [campaignId, cursor, hasMore, isFetching, fetchPage, sortBy, sortOrder]);

  useEffect(() => {
    const anchor = loadMoreAnchorRef.current;
    if (!anchor || !hasMore || !cursor) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: '220px' },
    );

    observer.observe(anchor);
    return () => observer.disconnect();
  }, [hasMore, cursor, loadMore]);

  const syncFormCustomers = useCallback(
    (numbers: PhoneNumber[]) => {
      const formatted: NumbersList[] = [
        {
          name: 'Imported Numbers',
          customers: numbers.map((cust) => ({
            phone: cust.number,
            id: cust.id,
            isValid: true,
            name: cust.name,
            ...(cust.metadata !== undefined ? { metadata: cust.metadata } : {}),
            ...(cust.attributes !== undefined ? { attributes: cust.attributes } : {}),
          })),
        },
      ];

      const customers = formatted.flatMap((list) => NumberListToCustomersMapper(list));

      setValue('customers', customers, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      } as Parameters<typeof setValue>[2]);

      setCustomersListChanged(true);
    },
    [setValue, setCustomersListChanged],
  );

  const normalizePhoneNumber = normalizeSipDialString;

  const hasDuplicateNumber = (targetNumber: string, excludeId?: string) => {
    const normalizedTarget = normalizePhoneNumber(targetNumber);
    return pagedCustomers.some(
      (n) => n.id !== excludeId && normalizePhoneNumber(n.number) === normalizedTarget,
    );
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedNumbers(realCustomers.map((n) => n.id));
    } else {
      setSelectedNumbers([]);
    }
  };

  const handleSelect = (id: string) => {
    setSelectedNumbers((prev) => {
      if (prev.includes(id) || id.startsWith('new')) return prev.filter((n) => n !== id);
      return [...prev, id];
    });
  };

  const handleAddNumber = () => setShowAddModal(true);

  const handleEditCustomerDataClick = (number: PhoneNumber) => {
    setEditingNumber(number);
    setShowEditModal(true);
  };

  const handleModalSave = (
    attributes?: Record<string, string>,
    metadata?: Record<string, string>,
    phoneNumber?: string,
  ) => {
    if (phoneNumber) {
      if (hasDuplicateNumber(phoneNumber)) {
        const errorMsg = 'This phone number is already added';
        toast.error((props) => <CustomErrorToast {...props} message={errorMsg} />, {
          closeButton: false,
          autoClose: 5000,
          icon: false,
        });
        return;
      }
      const newPhoneNumber: PhoneNumber = {
        id: `new-${Date.now()}`,
        number: normalizePhoneNumber(phoneNumber),
        name: attributes?.name || null,
        dateAdded: new Date().toLocaleDateString(),
        ...(attributes && Object.keys(attributes).length > 0 ? { attributes } : {}),
        ...(metadata && Object.keys(metadata).length > 0 ? { metadata } : {}),
      };
      const updated = [...pagedCustomers, newPhoneNumber];
      setPagedCustomers(updated);
      syncFormCustomers(updated);
      setShowAddModal(false);
      return;
    }

    if (!editingNumber) return;
    const updated = pagedCustomers.map((n) =>
      n.id === editingNumber.id
        ? {
            ...n,
            attributes: attributes ?? {},
            metadata: metadata ?? {},
            name: attributes?.name ?? null,
          }
        : n,
    );
    setPagedCustomers(updated);
    syncFormCustomers(updated);
    setShowEditModal(false);
    setEditingNumber(null);
  };

  const handleDeleteClick = (number?: PhoneNumber) => {
    if (selectedNumbers.length > 0) {
      setDeleteIds(selectedNumbers);
    } else if (number) {
      setDeleteIds([number.id]);
    }
    setShowDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const updated = pagedCustomers.filter((n) => !deleteIds.includes(n.id));
      setPagedCustomers(updated);
      syncFormCustomers(updated);

      const successMsg = t('phoneNumbers.list.messages.deleteSuccess', { count: deleteIds.length });
      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('common.saved'),
          description: successMsg,
        },
      });
    } catch {
      const errorMsg = 'Failed to delete phone number';
      toast.error((props) => <CustomErrorToast {...props} message={errorMsg} />, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
      });
    }
    setShowDialog(false);
    setDeleteIds([]);
    setSelectedNumbers((prev) => prev.filter((id) => !deleteIds.includes(id)));
    setAvailableToImport(false);
  };

  const handleFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(10);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        setUploadProgress(30);
        const result = e.target?.result;
        if (!result || typeof result === 'string') return;
        const data = new Uint8Array(result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        setUploadProgress(50);
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet) as Record<string, unknown>[];

        setUploadProgress(70);
        const existingNormalized = new Set(
          pagedCustomers
            .map((n) => n.number)
            .filter(Boolean)
            .map((n) => normalizePhoneNumber(n)),
        );

        const newNumbers: PhoneNumber[] = rows
          .map((row): PhoneNumber | null => {
            const phoneStr = row.phone?.toString().trim();
            if (!phoneStr || !isValidSipDialString(phoneStr)) return null;

            const number = normalizeSipDialString(phoneStr);

            const attributes: Record<string, string> = {};
            const metadata: Record<string, string> = {};
            let name: string | null = null;

            for (const [key, value] of Object.entries(row)) {
              if (key === 'phone') continue;
              if (value === undefined || value === null || value === '') continue;
              const trimmed = String(value).trim();
              if (key.startsWith('attr')) attributes[key] = trimmed;
              else if (key.startsWith('meta')) metadata[key] = trimmed;
              else if (key === 'name') name = trimmed;
              else metadata[key] = trimmed;
            }

            return {
              id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              number,
              name,
              dateAdded: new Date().toLocaleDateString(),
              ...(Object.keys(attributes).length ? { attributes } : {}),
              ...(Object.keys(metadata).length ? { metadata } : {}),
            };
          })
          .filter((entry): entry is PhoneNumber => {
            if (!entry) return false;
            const norm = normalizePhoneNumber(entry.number);
            if (existingNormalized.has(norm)) return false;
            existingNormalized.add(norm);
            return true;
          });

        setUploadProgress(90);
        const updated = [...pagedCustomers, ...newNumbers];
        setPagedCustomers(updated);
        syncFormCustomers(updated);

        const successMsg = `Successfully imported ${newNumbers.length} phone number${newNumbers.length > 1 ? 's' : ''}`;
        toast.success(CustomSuccessToast, {
          closeButton: false,
          autoClose: 5000,
          icon: false,
          data: { title: t('common.saved'), description: successMsg },
        });
        setUploadProgress(100);
      };

      reader.readAsArrayBuffer(file);
    } catch {
      const errorMsg = 'Failed to import phone numbers';
      toast.error((props) => <CustomErrorToast {...props} message={errorMsg} />, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (event.target) event.target.value = '';
    }
  };

  const handleExport = () => {
    const dataToExport = pagedCustomers.map(({ number, name, attributes, metadata }) => {
      const row: Record<string, string> = { phone: number, name: name || '' };
      if (attributes) Object.entries(attributes).forEach(([k, v]) => (row[k] = v));
      if (metadata) Object.entries(metadata).forEach(([k, v]) => (row[k] = v));
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Customers');
    XLSX.writeFile(wb, 'customers.xlsx');
  };

  const allRealCustomers = pagedCustomers.filter((n) => n.number);
  const realCustomers = searchText.trim()
    ? allRealCustomers.filter((n) => {
        const q = searchText.trim().toLowerCase();
        return (n.name ?? '').toLowerCase().includes(q) || n.number.toLowerCase().includes(q);
      })
    : allRealCustomers;
  const actionsDisabled =
    currentUser?.role === UserRolesEnum.VIEWER || isCampaignActive || !availableToImport;
  const selectionDisabled = currentUser?.role === UserRolesEnum.VIEWER || isCampaignActive;

  const formatDateWithDots = (value: string) => value.replace(/\//g, '.');

  const checkboxIcon = (
    <Box
      sx={(theme) => ({
        width: 18,
        height: 18,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        backgroundColor: theme.palette.background.paper,
      })}
    />
  );
  const checkboxCheckedIcon = (
    <Box
      sx={(theme) => ({
        width: 18,
        height: 18,
        borderRadius: '4px',
        backgroundColor: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      })}>
      <CheckIcon sx={{ fontSize: 14, color: '#FFFFFF' }} />
    </Box>
  );
  const checkboxSx = { p: 0.5 };

  const columns: CommonTableColumn<PhoneNumber>[] = [
    {
      id: 'select',
      hideRightBorder: true,
      header: (
        <Checkbox
          checked={selectedNumbers.length === realCustomers.length && !!realCustomers.length}
          indeterminate={
            selectedNumbers.length > 0 && selectedNumbers.length < realCustomers.length
          }
          onChange={handleSelectAll}
          disabled={selectionDisabled || !realCustomers.length}
          icon={checkboxIcon}
          checkedIcon={checkboxCheckedIcon}
          sx={checkboxSx}
        />
      ),
      padding: 'checkbox',
      renderCell: (number) => (
        <Checkbox
          checked={selectedNumbers.includes(number.id)}
          onChange={() => handleSelect(number.id)}
          disabled={selectionDisabled}
          icon={checkboxIcon}
          checkedIcon={checkboxCheckedIcon}
          sx={checkboxSx}
        />
      ),
    },
    {
      id: 'phone',
      header: (
        <TableSortLabel
          active={sortBy === 'name'}
          direction={getSortDirection('name')}
          onClick={() => handleSort('name')}
          IconComponent={SortIcon}
          sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
          Phone number
        </TableSortLabel>
      ),
      headerSx: { borderLeft: 'none !important' },
      cellSx: { borderLeft: 'none !important' },
      renderCell: (number) => number.number || formatCallType(CALL_TYPES.WEB_CALL),
    },
    {
      id: 'name',
      header: (
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
          Name
        </Typography>
      ),
      renderCell: (number) => number.name ?? '—',
    },
    {
      id: 'dateAdded',
      hideRightBorder: true,
      header: (
        <TableSortLabel
          active={sortBy === 'createdAt'}
          direction={getSortDirection('createdAt')}
          onClick={() => handleSort('createdAt')}
          IconComponent={SortIcon}
          sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
          Date added
        </TableSortLabel>
      ),
      renderCell: (number) => formatDateWithDots(number.dateAdded ?? ''),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      renderCell: (number) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          <Tooltip title="Edit" arrow placement="top">
            <span>
              <IconButton
                onClick={() => handleEditCustomerDataClick(number)}
                disabled={actionsDisabled}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#F3F6FB',
                }}>
                <IconEditPen />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete" arrow placement="top">
            <span>
              <IconButton
                onClick={() => handleDeleteClick(number)}
                disabled={actionsDisabled}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#FFF7F5',
                }}>
                <IconTrash />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Box sx={{ p: 2, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 2,
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 1,
            minWidth: 0,
          }}>
          <Typography variant="h5" sx={{ minWidth: 0 }}>
            Customers ({realCustomers.length})
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              flexShrink: 1,
              minWidth: 0,
              maxWidth: '100%',
            }}>
            <Button
              variant="outlined"
              onClick={handleExport}
              disabled={!availableToImport}
              startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
              sx={{ textTransform: 'none' }}>
              Export
            </Button>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileOutlinedIcon fontSize="small" />}
              sx={{ textTransform: 'none' }}
              disabled={
                !availableToImport || currentUser?.role === UserRolesEnum.VIEWER || isCampaignActive
              }>
              Import
              <input type="file" hidden accept=".xlsx,.xls" onChange={handleFileImport} />
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNumber}
              sx={{ textTransform: 'none' }}
              disabled={
                !availableToImport || currentUser?.role === UserRolesEnum.VIEWER || isCampaignActive
              }>
              Add Customer
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            minWidth: 0,
          }}>
          <Typography color="text.secondary">{selectedNumbers.length} selected</Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              minWidth: 0,
              maxWidth: '100%',
            }}>
            <TextField
              size="small"
              placeholder="Filter by name or number"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ width: { xs: '100%', sm: 220, md: 300 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              color="error"
              onClick={() => handleDeleteClick()}
              disabled={!selectedNumbers.length || selectionDisabled}
              sx={{ textTransform: 'none' }}>
              Delete
            </Button>
          </Box>
        </Box>

        {isUploading && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}

        <CommonTable
          columns={columns}
          rows={realCustomers}
          rowKey={(row) => row.id}
          selectedRowIds={selectedNumbers}
          containerSx={(theme) => ({
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
          emptyState={
            pagedCustomers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  {isInitialLoading || isFetching ? (
                    <CircularProgress size={32} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('campaignPage.noPhoneNumbers')}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ) : null
          }
        />
        <Box ref={loadMoreAnchorRef} sx={{ height: 1 }} />
        {isFetching && pagedCustomers.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>

      <Dialog open={showDialog} onClose={() => setShowDialog(false)}>
        <DialogTitle>
          {t('phoneNumbers.list.deleteDialog.title', { count: deleteIds.length })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('phoneNumbers.list.deleteDialog.description', { count: deleteIds.length })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)}>{t('common.cancel')}</Button>
          <Button onClick={confirmDelete} color="error" autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {responseMsg.message && <CustomAlert message={responseMsg.message} type={responseMsg.type} />}

      <EditAttributesMetadataModal
        open={showAddModal}
        mode="add"
        onClose={() => setShowAddModal(false)}
        onSave={handleModalSave}
      />

      <EditAttributesMetadataModal
        open={showEditModal}
        mode="edit"
        onClose={() => {
          setShowEditModal(false);
          setEditingNumber(null);
        }}
        onSave={handleModalSave}
        initialPhoneNumber={editingNumber?.number || ''}
        initialAttributes={editingNumber?.attributes}
        initialMetadata={editingNumber?.metadata}
      />
    </>
  );
};

PhoneNumberList.propTypes = {
  setCustomersListChanged: PropTypes.func.isRequired,
};
